<template>
    <BaseModal
        size="modal-lg"
        class="edit-order form"
        :closable="true"
        ref="baseModalComponent"
        :title="translations.editOrder.editOrder"
        :has-header="true"
    >
        <template v-slot:body>
            <div class="edit-order__content">
                <!-- Left Column: Reservation Details -->
                <div class="edit-order__left-column">
                    <div class="edit-order-date-time">
                        <div v-if="fulfilmentReadyDateTime != null">
                            <div class="order-time-info">
                                <div class="fulfilment-ready-date">
                                    <i class="fa-light fa-person-carry-box"></i>{{ fulfilmentReadyDateTime
                                    }}<i
                                        v-if="!editPreorderDateTime"
                                        class="fa-light fa-edit"
                                        @click="onClickEditPreorderDateTime"
                                    ></i>
                                </div>
                            </div>
                        </div>

                        <OrderDateTimeForm
                            v-model="selectedOrderDateAndTime"
                            class="mt-2"
                            ref="orderDateTimeFormComponent"
                        />

                        <button
                            class="mt-1 cancel-editing-preorder-date-time-btn"
                            v-if="editPreorderDateTime"
                            @click="onClickCancelEditingPreorderDateTime"
                        >
                            {{ translations.general.cancel }}
                        </button>
                    </div>

                    <!-- Order items -->
                    <div class="edit-order__order-items">
                        <Dropdown
                            v-model="selectedArticle"
                            :options="selectListArticles"
                            optionLabel="name"
                            class="w-100"
                        >
                            <template #value="article">
                                <div
                                    v-if="article.value"
                                    class="edit-order__order-items-item d-flex justify-content-between"
                                >
                                    <div>{{ getDisplayName(article.value) }}</div>
                                    <div style="font-weight: 600">
                                        {{ getDisplayPrice(article.value) }}
                                    </div>
                                </div>
                            </template>
                            <template #option="article">
                                <div
                                    v-if="article.option"
                                    class="edit-order__order-items-item d-flex justify-content-between"
                                >
                                    <div>{{ getDisplayName(article.option) }}</div>
                                    <div style="font-weight: 600">
                                        {{ getDisplayPrice(article.option) }}
                                    </div>
                                </div>
                            </template>
                        </Dropdown>

                        <!-- Special Request -->
                        <div
                            v-if="
                                selectedOrderItem != null &&
                                selectedOrderItem.canHaveComments &&
                                currentOrder?.areItemCommentsAllowed === true &&
                                selectedReservation?.acceptanceState !== ReservationAcceptanceState.Declined
                            "
                            class="mt-2"
                        >
                            <div v-if="selectedOrderItem.comment != null">
                                <textarea
                                    v-model="selectedOrderItem.comment.text"
                                    class="edit-order__textarea"
                                    maxlength="120"
                                    :placeholder="translations.cart.addComment"
                                >
                                </textarea>
                                <button class="remove-order-item-comment-btn" @click="removeOrderItemComment">
                                    {{ translations.general.remove }}
                                </button>
                            </div>
                            <div v-else>
                                <button class="add-order-item-comment-btn" @click="addOrderItemComment">
                                    {{ translations.cart.addComment }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Right Column: Contact Information and Table Selection -->
                <div class="edit-order__right-column">
                    <!-- Name Fields -->
                    <div v-if="currentOrder != null" class="edit-order__name-fields">
                        <TextInput
                            :label="translations.general.firstName"
                            v-model:value="currentOrder!.indirectIdentityFirstName"
                        />
                        <TextInput
                            :label="translations.general.lastName"
                            v-model:value="currentOrder!.indirectIdentityLastName"
                        />
                    </div>

                    <!-- Phone Number -->
                    <div class="edit-order__section">
                        <div class="edit-order__phone-container">
                            <PhoneNumberInput
                                :requirement-type="InputRequirementType.NotRequired"
                                ref="phoneNumberComponent"
                            />
                        </div>
                    </div>

                    <!-- Email -->
                    <div v-if="currentOrder != null" class="edit-order__section">
                        <EmailInput v-model:value="currentOrder.indirectIdentityEmail" />
                    </div>

                    <!-- Table Selection -->
                    <div class="edit-order__section">
                        <div class="edit-order__table-selection">
                            <ReservationTablesList
                                v-if="
                                    currentPreorder != null &&
                                    selectedReservation != null &&
                                    selectedReservation.tableAssignmentAvailable
                                "
                                :isReadonly="!selectedReservation.canEditTableAssignments"
                                ref="reservationTablesList"
                                :orderId="currentPreorder.orderId"
                                :reservationDetails="selectedReservation"
                                @reservationDetailsChanged="onReservationDetailsChanged"
                            />
                        </div>
                    </div>

                    <ReservationStatesGrid
                        v-if="selectedReservation"
                        :order-id="props.orderId"
                        :reservationDetails="selectedReservation"
                        @on-operation-failed="onReservationStateOperationFailed"
                        @on-reservation-state-changed="onReservationDetailsChanged"
                    />
                </div>
            </div>
        </template>
        <template v-slot:footer>
            <button type="button" class="btn btn-secondary" @click="onClickClose">
                {{ translations.general.close }}
            </button>

            <button type="button" @click="onClickSave" class="btn btn-success">
                {{ translations.general.save }}
            </button>
        </template>
    </BaseModal>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { InputRequirementType } from "@/components/elements/InputRequirementType";
    import type { IArticleWithOptions } from "@/data/commands/customer/AddArticleToCartCmd";
    import { Factory } from "@/data/dao/Factory";
    import { DaoError } from "@/data/dao/service/BaseServiceDao";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import { OrderDao } from "@/data/dao/service/OrderDao";
    import type {
        ICustomerArticle,
        ICustomerArticleWithOptions,
        ICustomerCategory,
        ICustomerOffer,
        SelectedOrderDateAndTime,
    } from "@/data/model/Customer";
    import { PredefinedResourceCode, ReservationAcceptanceState } from "@/data/model/Enums";
    import type { IFullOrder, IOrderItem, IPreorder, IReservationDetails } from "@/data/model/Order";
    import { useIdentityStore } from "@/data/stores/identity";
    import { useOfferStore } from "@/data/stores/offer";
    import { exceptionTypes } from "@/data/util/Config";
    import { DateTime } from "@/data/util/DateTime";
    import { Format } from "@/data/util/Format";
    import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";
    import { operatorEvents } from "@/operator/events/operatorEvents";
    import { UiNotification } from "@/portal/ui/Notification";
    import { translations as trans, translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { toMoney } from "@/shared/utils/format";
    import { computed, onMounted, ref } from "vue";

    import BaseModal from "@/components/dialog/BaseModal.vue";
    import EmailInput from "@/components/elements/EmailInput.vue";
    import PhoneNumberInput from "@/components/elements/PhoneNumberInput.vue";
    import TextInput from "@/components/elements/TextInput.vue";
    import OrderDateTimeForm from "@/components/preorder/OrderDateTimeForm.vue";
    import ReservationStatesGrid from "@/components/preorder/ReservationStatesGrid.vue";
    import ReservationTablesList from "@/operator/components/order/ReservationTablesList.vue";
    import Dropdown from "primevue/dropdown";

    import { getPreselectedOptions } from "./preselectedArticleOptions";

    const orderDao = Factory.getInstance<OrderDao>(OrderDao);
    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    const identity = useIdentityStore();
    const offerStore = useOfferStore();

    const props = defineProps<{
        orderId: string;
        onPreorderUpdated?: () => void;
    }>();

    const emit = defineEmits(["preorderUpdated"]);

    const orderDateTimeFormComponent = ref<InstanceType<typeof OrderDateTimeForm> | null>(null);
    const selectedOrderDateAndTime = defineModel<SelectedOrderDateAndTime | null>("selectedOrderDateAndTime", {
        required: true,
    });

    // Computed
    const fulfilmentReadyDateTime = computed(() => {
        if (currentOrder.value?.scheduledFulfillmentDateTime != null) {
            return DateTime.toPreciseDateTimeString(currentOrder.value.scheduledFulfillmentDateTime);
        } else {
            return null;
        }
    });

    // Reactive data
    const currentOrder = ref<IFullOrder | null>(null);
    const currentPreorder = ref<IPreorder | null>(null);
    const baseModalComponent = ref<InstanceType<typeof BaseModal> | null>(null);
    const phoneNumberComponent = ref<InstanceType<typeof PhoneNumberInput> | undefined>();
    const selectedOrderItem = ref<IOrderItem | null>(null);
    const selectedReservation = ref<IReservationDetails | null>(null);
    const editPreorderDateTime = ref<boolean>(false);
    const currentOffer = ref<ICustomerOffer | null>(null);
    const selectListArticles = ref<ICustomerArticleWithOptions[]>([]);
    const selectedArticle = ref<ICustomerArticleWithOptions | null>(null);
    const orderedArticles = ref<ICustomerArticle[]>([]);
    const articlesWithOptions = ref<ICustomerArticleWithOptions[]>([]);
    const originalArticleId = ref<string>();
    const articleMenuCategoryMap = ref<Map<string, ICustomerCategory>>(new Map());

    onMounted(init);

    // Methods
    async function init() {
        // Fetch data
        currentOrder.value = await orderDao.getSingle(props.orderId, {
            showLocationOrderItemsIfLocationIsNotOwner: false,
        });
        currentPreorder.value = await operatorDao.getPreorder(props.orderId, {
            orderId: props.orderId,
            includeCancelledReservations: true,
        });
        currentOffer.value = await customerDao.getOffer({
            offerId: currentPreorder.value?.offerId,
            useCaseId: currentPreorder.value?.orderUseCaseId,
            locationId: currentPreorder.value?.locationId,
            useCartOffer: false,
            shouldGetOnlyOffer: true,
        });

        const { articles, articleMenuCategoryDict } = getOrderedArticles();
        orderedArticles.value = articles;
        articleMenuCategoryMap.value = articleMenuCategoryDict;
        articlesWithOptions.value = offerStore.getArticlesWithOptions(orderedArticles.value, currentOffer.value);

        // Order has always one item
        selectedOrderItem.value = currentOrder.value.items[0];
        const foundReservation = currentPreorder.value!.reservation;

        selectedReservation.value = foundReservation === undefined ? null : foundReservation;
        originalArticleId.value = selectedReservation.value?.overridingArticleId ?? selectedOrderItem.value!.articleId;

        selectedArticle.value = articlesWithOptions.value.find((a) => a.articleId === originalArticleId.value)!;
        phoneNumberComponent.value?.setValue(currentOrder.value.indirectIdentityPhoneNumber);

        const isOriginalArticleTableReservation = isTableReservation(selectedArticle.value);
        selectListArticles.value = filterArticles(articlesWithOptions.value, isOriginalArticleTableReservation);
    }

    function filterArticles(
        articles: ICustomerArticleWithOptions[],
        isOriginalArticleTableReservation: boolean
    ): ICustomerArticleWithOptions[] {
        // If the original article is a table reservation, show only table reservation articles
        // Otherwise, show only the original article
        return articles.filter(
            (a) =>
                (isOriginalArticleTableReservation && isTableReservation(a)) || a.articleId === originalArticleId.value
        );
    }

    function isTableReservation(article: ICustomerArticleWithOptions): boolean {
        return article.resources.some((r) => r.predefinedResourceCode === PredefinedResourceCode.Seats);
    }

    function getOrderedArticles(): {
        articles: ICustomerArticle[];
        articleMenuCategoryDict: Map<string, ICustomerCategory>;
    } {
        const orderedArticles: ICustomerArticle[] = [];
        const articleMenuCategoryDict = new Map<string, ICustomerCategory>();
        for (const entry of currentOffer.value?.entries ?? []) {
            for (const article of entry.articles) {
                if (orderedArticles.find((a) => a.articleId === article.articleId)) {
                    continue;
                }
                orderedArticles.push(article);
                articleMenuCategoryDict.set(article.articleId, entry);
            }
        }
        return { articles: orderedArticles, articleMenuCategoryDict };
    }

    function onClickClose() {
        close();
    }

    async function onClickSave() {
        const orderToSave = currentOrder.value!;
        const phoneNumberResult = phoneNumberComponent.value?.getResult();

        let isDataValid = true;

        if (phoneNumberComponent.value != null) {
            if (!phoneNumberResult!.isValid) {
                isDataValid = false;
                phoneNumberComponent.value!.setError(true);
            }
        }

        if (isDataValid) {
            try {
                const articleWithOptions: IArticleWithOptions = {
                    articleId: selectedArticle.value!.articleId,
                    menuCategoryId: articleMenuCategoryMap.value.get(selectedArticle.value!.articleId)?.categoryId,
                    options: getPreselectedOptions(selectedArticle.value!),
                };

                await orderDao.update(orderToSave.id, {
                    newPreorderDateTime: editPreorderDateTime.value
                        ? {
                              preorderTime: selectedOrderDateAndTime.value!.time,
                              preorderDate: selectedOrderDateAndTime.value!.date,
                          }
                        : undefined,
                    indirectIdentityEmail: orderToSave.indirectIdentityEmail,
                    indirectIdentityFirstName: orderToSave.indirectIdentityFirstName,
                    indirectIdentityLastName: orderToSave.indirectIdentityLastName,
                    indirectIdentityPhoneNumber: phoneNumberResult?.phoneNumber,
                    orderId: orderToSave.id,
                    orderItems: orderToSave.items.map((item) => {
                        return {
                            orderItemId: item.id,
                            articleWithOptions: articleWithOptions,
                            comment: item.comment,
                        };
                    }),
                });

                emit("preorderUpdated");
                if (props.onPreorderUpdated) {
                    props.onPreorderUpdated();
                }
                operatorEvents.editPreorderPopup.preorderUpdated.dispatch();

                close();
            } catch (ex) {
                logError(ex);

                if (ex instanceof DaoError) {
                    if (ex.type === exceptionTypes.tableReservationNoAvailabilityException) {
                        UiNotification.showException(trans.pay.noFulfillmentSlotAvailable);
                        return;
                    }
                }

                UiNotification.showException(translations.general.operationFailed);
            }
        }
    }

    function show() {
        baseModalComponent.value?.show();
    }

    function close() {
        baseModalComponent.value?.close();
    }

    function getDisplayName(orderItem: IOrderItem) {
        return Format.htmlEscape(getNameInLocationLanguage(orderItem, true));
    }

    function getDisplayPrice(orderItem: IOrderItem) {
        if (orderItem.unitPrice === 0) return "";

        return toMoney(orderItem.isCancelled ? orderItem.originalTotalAmount : orderItem.currentTotalAmount, true);
    }

    function addOrderItemComment() {
        selectedOrderItem.value!.comment = {
            identityId: identity.identityId!,
            text: "",
        };
    }

    function removeOrderItemComment() {
        selectedOrderItem.value!.comment = undefined;
    }

    function onReservationDetailsChanged(newReservationDetails: IReservationDetails) {
        if (currentPreorder.value!.reservation?.orderItemId !== newReservationDetails.orderItemId) {
            throw new Error(`Reservation with order item id ${newReservationDetails.orderItemId} was not found`);
        }

        // update original row
        currentPreorder.value!.reservation = newReservationDetails;
        selectedReservation.value = newReservationDetails;
    }

    function onClickEditPreorderDateTime(e) {
        e.preventDefault();
        editPreorderDateTime.value = true;

        orderDateTimeFormComponent.value?.initialize(
            {
                date: currentOrder.value!.scheduledFulfillmentDateTime,
                time: DateTime.toTimeString(currentOrder.value!.scheduledFulfillmentDateTime!),
                offerId: currentOrder.value!.offerId,
            } as SelectedOrderDateAndTime,
            currentOrder.value!.serviceId,
            $(".edit-order")
        );
    }

    function onClickCancelEditingPreorderDateTime(e) {
        e.preventDefault();
        orderDateTimeFormComponent.value?.reset();
        editPreorderDateTime.value = false;
    }

    function onReservationStateOperationFailed() {
        Popup.show(
            {
                zIndex: 30_000,
                message: translations.general.operationFailed,
            },
            $(".edit-order")
        );
    }

    // Expose data for parent component
    defineExpose({
        show,
        close,
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .edit-order {
        .quantity-input {
            margin: 0 auto 21px auto;

            button.circle-button {
                background-color: colors.$green !important;

                &:disabled {
                    cursor: default;
                }

                @include mixins.hover-and-active() {
                    background-color: colors.$greenHover;
                }
            }
        }

        .add-order-item-comment-btn,
        .remove-order-item-comment-btn,
        .cancel-editing-preorder-date-time-btn {
            display: inline-block;
            cursor: pointer;
            background: transparent;
            transition: all 0.2s variables.$easeOutExpo;
            padding: 0.5em;
            margin: -0.5em 0.25em 0 0;
            border-radius: 0.25rem;
            color: colors.$green;

            @include mixins.hover-and-active {
                text-underline-offset: 0.25em;
                color: colors.$greenHover;
            }

            &.disabled {
                cursor: default !important;
                text-decoration: none !important;
            }
        }

        .fulfilment-ready-date {
            padding-top: 4px;
            font-size: 14px;
            line-height: 16px;
            font-weight: 600;
            color: #aaaaaa;

            > .fa-person-carry-box {
                background-color: colors.$green;
                color: white;
                padding: 4px;
                border-radius: 2px;
                margin-right: 6px;
            }

            > .fa-edit {
                background-color: colors.$white;
                margin-left: 6px;
                color: colors.$green;
                font-size: 1.2rem;
                padding: 4px;
            }
        }

        .edit-order-date-time {
            margin-bottom: 4px;
        }
    }

    .edit-order__content {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        padding: 1.25rem;
        background: white;
    }

    .edit-order__left-column,
    .edit-order__right-column {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    .edit-order__section {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .edit-order__input-container {
        position: relative;

        label {
            font-weight: bold;
            margin-bottom: 0.5em;
            display: inline-block;
        }
    }

    .edit-order__input {
        width: 100%;
        padding: 12px 16px;
        border: 1px solid #ced4da;
        border-radius: 6px;
        font-size: 14px;
        background: white;
        transition: all 0.2s ease;
        box-sizing: border-box;
    }

    .edit-order__input:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    }

    .edit-order__input--additional {
        background: #f8f9fa;
    }

    .edit-order__order-items {
        width: 300px;
    }

    .edit-order__order-items-item {
        font-size: 16px;
        padding: 4px 8px;
        white-space: break-spaces;
    }

    .edit-order__summary-text {
        font-size: 16px;
        color: #495057;
        font-weight: 500;
    }

    .edit-order__price {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .edit-order__price-amount {
        font-size: 18px;
        font-weight: 600;
        color: #28a745;
    }

    .edit-order__checkmark {
        color: #28a745;
        font-size: 16px;
        font-weight: bold;
    }

    .edit-order__quantity-btn {
        width: 40px;
        height: 40px;
        border: 2px solid #007bff;
        border-radius: 50%;
        background: white;
        color: #007bff;
        font-size: 18px;
        font-weight: bold;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .edit-order__quantity-btn:hover:not(:disabled) {
        background: #007bff;
        color: white;
    }

    .edit-order__quantity-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        border-color: #6c757d;
        color: #6c757d;
    }

    .edit-order__guest-count {
        font-size: 18px;
        font-weight: 600;
        color: #495057;
        min-width: 30px;
        text-align: center;
    }

    .edit-order__textarea {
        width: 100%;
        padding: 12px 16px;
        border: 1px solid #ced4da;
        border-radius: 6px;
        font-size: 14px;
        background: white;
        transition: all 0.2s ease;
        resize: vertical;
        min-height: 80px;
        font-family: inherit;
        box-sizing: border-box;
    }

    .edit-order__textarea:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    }

    .edit-order__name-fields {
        display: flex;
        gap: 12px;
    }

    .edit-order__name-fields .edit-order__input {
        flex: 1;
    }

    .edit-order__phone-container {
        display: flex;
    }

    .edit-order__table-selection {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }

    .edit-order__table {
        padding: 2px 8px;
        border-radius: 6px;
        color: colors.$tastierDarkGrey;
        background: colors.$tastierLightGrey;
        font-size: 12px;
        font-weight: 500;
    }

    /* Responsive Design */
    @media (max-width: 768px) {
        .edit-order__content {
            grid-template-columns: 1fr;
            gap: 20px;
            padding: 20px;
        }

        .edit-order__name-fields {
            flex-direction: column;
        }

        .edit-order__quantity-selector {
            gap: 15px;
        }

        .edit-order__table-selection {
            flex-direction: column;
        }
    }

    @media (max-width: 480px) {
        .edit-order__content {
            padding: 15px;
        }
    }
</style>
